import { Entity } from '@backstage/catalog-model';
import { TableColumn } from '@backstage/core-components';
import { CatalogTableRow } from '@backstage/plugin-catalog';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import Box from '@mui/material/Box';
import PersonIcon from '@mui/icons-material/Person';
import {
  BU_ANNOTATION,
  PU_ANNOTATION,
  annotationValue,
  emailToUserRef,
  matchesSearch,
  stripManagerSuffix,
} from './utils';

const displayName = (entity: Entity) =>
  stripManagerSuffix(entity.metadata.title ?? entity.metadata.name);

const firstManager = (entity: Entity) => {
  const managers = (entity.metadata as Record<string, unknown>).managers as
    | { name?: string; email?: string }[]
    | undefined;
  return managers?.[0];
};

export const groupColumns: TableColumn<CatalogTableRow>[] = [
  {
    title: 'Name',
    field: 'resolved.entityRef',
    highlight: true,
    customSort({ entity: e1 }, { entity: e2 }) {
      return displayName(e1).localeCompare(displayName(e2));
    },
    customFilterAndSearch: (term, { entity }) =>
      matchesSearch(term, displayName(entity), entity.metadata.name),
    render: ({ entity }) => (
      <EntityRefLink entityRef={entity} defaultKind="group">
        {displayName(entity)}
      </EntityRefLink>
    ),
  },
  {
    title: 'Manager',
    field: 'entity.metadata.managers',
    searchable: false,
    render: ({ entity }) => {
      const manager = firstManager(entity);
      if (!manager?.name) return <>—</>;
      const ref = manager.email ? emailToUserRef(manager.email) : null;
      const content = (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon fontSize="small" style={{ marginRight: 8 }} />
          <span>{manager.name}</span>
        </Box>
      );
      return ref ? (
        <EntityRefLink entityRef={ref} defaultKind="user" hideIcon>
          {content}
        </EntityRefLink>
      ) : (
        <>{content}</>
      );
    },
  },
  {
    title: 'Business Unit',
    field: `entity.metadata.annotations.${BU_ANNOTATION}`,
    searchable: false,
    render: ({ entity }) => annotationValue(entity, BU_ANNOTATION),
  },
  {
    title: 'Product Unit',
    field: `entity.metadata.annotations.${PU_ANNOTATION}`,
    searchable: false,
    render: ({ entity }) => annotationValue(entity, PU_ANNOTATION),
  },
  {
    title: 'Type',
    field: 'entity.spec.type',
    searchable: false,
    render: ({ entity }) => {
      const type = entity.spec?.type;
      return typeof type === 'string' ? type : '—';
    },
  },
];
